/**
 * Per-navigation correlation context shared by the redirect page and the visit
 * tracker, plus a small coordination helper so a redirect never cuts the
 * associated visit recording short without blocking the user either.
 *
 * The navigation is identified by the router navigation key (React Router's
 * `location.key`), not by the URL: two successive navigations to the same URL
 * are two navigations and get two trace ids.
 */

const SESSION_ID_KEY = "visitor_session_id";

/** RFC 4122 v4, required because visitor_tracking.session_id is a uuid column. */
export function uuidv4(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    /* fall through to the Math.random variant */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Stable in-memory fallback when sessionStorage is unavailable (private mode…). */
let memorySessionId: string | null = null;

/** Stable session identifier, created before first use by either consumer. */
export function getSessionId(): string {
  if (typeof window === "undefined") {
    memorySessionId ??= uuidv4();
    return memorySessionId;
  }
  try {
    let id = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = memorySessionId ?? uuidv4();
      window.sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    memorySessionId = id;
    return id;
  } catch {
    memorySessionId ??= uuidv4();
    return memorySessionId;
  }
}

export type TraceContext = {
  navKey: string;
  traceId: string;
  sessionId: string;
};

let current: TraceContext | null = null;

/**
 * Correlation context for one navigation, shared by every consumer of that
 * navigation (Redirect + VisitorTracker). Must be captured before any await.
 */
export function getNavigationContext(navKey: string): TraceContext {
  if (!current || current.navKey !== navKey) {
    current = { navKey, traceId: `t_${uuidv4()}`, sessionId: getSessionId() };
  }
  return current;
}

/** Test seam. */
export function __resetTraceState() {
  current = null;
  memorySessionId = null;
  pending.clear();
}

/** Bound used when a redirect waits for the in-flight visit recording. */
export const PENDING_VISIT_BUDGET_MS = 1200;

const pending = new Set<Promise<unknown>>();

/** Register a fire-and-forget tracking call so a redirect can wait for it. */
export function registerPendingVisit(promise: Promise<unknown>): void {
  const p = promise.catch(() => undefined);
  pending.add(p);
  void p.finally(() => pending.delete(p));
}

/**
 * Waits at most `budgetMs` for in-flight visit recordings. Always resolves:
 * a tracking failure or timeout must never prevent the redirect.
 */
export async function awaitPendingVisits(budgetMs = PENDING_VISIT_BUDGET_MS): Promise<void> {
  if (pending.size === 0) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, budgetMs);
  });
  await Promise.race([Promise.allSettled([...pending]).then(() => undefined), deadline]);
  if (timer) clearTimeout(timer);
}
